import * as React from 'react'
import moment from 'moment'

import { TextInput } from '../TextInput'
import { DatePicker } from '../DatePicker'
import { BaseList } from '../List'
import { Container, DatePopover, DecorationContainer, OptionsPopover } from './Styles'
import { ReactComponent as ArrowDown } from 'assets/images/SMArrowDown.svg'
import { ReactComponent as ArrowUp } from 'assets/images/SMArrowUp.svg'
import { ReactComponent as ArrowLeft } from 'assets/images/XSArrowLeft.svg'
import { ReactComponent as ArrowRight } from 'assets/images/XSArrowRight.svg'
import { MAX_NEXT_MONTHS, MAX_PREVIOUS_MONTHS } from 'components/DatePicker/DatePicker'

export const DISPLAY_FORMAT = 'DD MMMM YYYY'
const OPTIONS = ['Arriving', 'Departing']

const stopPropagation = (e: React.MouseEvent<HTMLElement>) => e.stopPropagation()

interface Props {
  byArrival: boolean
  inputValue: moment.Moment | null
  handleChangeDate: (val: moment.Moment | null) => void
  handleChangeByArrival: (val: boolean) => void
}

type DateDropdownProps = Props & React.InputHTMLAttributes<HTMLInputElement>

const DateDropdown = ({
  byArrival,
  inputValue,
  handleChangeDate,
  handleChangeByArrival,
  ...rest
}: DateDropdownProps) => {
  const [show, setShow] = React.useState(false)
  const [showDatepicker, setShowDatepicker] = React.useState(false)
  const [selectedDate, setSelectedDate] = React.useState<moment.Moment | null>(null)
  const inputRef = React.useRef<HTMLInputElement>(null!)
  const [minMonth, maxMonth] = React.useMemo(() => {
    const prevMonth = moment(inputValue ? inputValue : undefined).subtract(
      MAX_PREVIOUS_MONTHS,
      'month'
    )
    const nextMonth = moment(inputValue ? inputValue : undefined).add(MAX_NEXT_MONTHS, 'month')
    return [prevMonth, nextMonth]
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => setSelectedDate(inputValue), [inputValue])

  const togglePrecomponentShow = React.useCallback(() => {
    setShow((prev) => !prev)
    setShowDatepicker(false)
  }, [])

  const PreDecoration = React.useCallback(
    () => (
      <DecorationContainer pointer={true} onClick={togglePrecomponentShow}>
        <p>{byArrival ? OPTIONS[0] : OPTIONS[1]}</p>
        {show ? <ArrowUp /> : <ArrowDown />}
      </DecorationContainer>
    ),
    [togglePrecomponentShow, byArrival, show]
  )

  const handleClickPostcomponent = React.useCallback(
    (dir: number) => {
      const currentMonth = inputValue!.month()
      const nextDate = inputValue!.clone().add(dir, 'day')
      const nextMonth = nextDate.month()
      const isEqual = currentMonth === nextMonth
      if(isEqual) {
        handleChangeDate(nextDate)
        setSelectedDate((prev) => prev?.clone().add(dir, 'day') ?? null)
      }
      const comparedDate = inputValue!.clone().add(dir, 'month')
      if(!comparedDate.isBetween(minMonth, maxMonth)) return
      handleChangeDate(nextDate)
      setSelectedDate((prev) => prev?.clone().add(dir, 'day') ?? null)
    },
    [inputValue, handleChangeDate, minMonth, maxMonth]
  )

  const PostDecoration = React.useCallback(
    () =>
      inputValue && !showDatepicker ? (
        <DecorationContainer>
          <span onClick={() => handleClickPostcomponent(-1)}>
            <ArrowLeft />
          </span>
          <span onClick={() => handleClickPostcomponent(1)}>
            <ArrowRight />
          </span>
        </DecorationContainer>
      ) : null,
    [handleClickPostcomponent, inputValue, showDatepicker]
  )

  const handleClickInput = React.useCallback(() => {
    setShowDatepicker(true)
    setShow(false)
  }, [])

  const handleClickOption = React.useCallback(
    (option: string) => {
      handleChangeByArrival(option === OPTIONS[0])
      setShow(false)
    },
    [handleChangeByArrival]
  )

  const handleSubmitDatepicker = React.useCallback(
    (selectedDate: moment.Moment) => {
      handleChangeDate(selectedDate)
      setShowDatepicker(false)
    },
    [handleChangeDate]
  )

  const handleClickCancel = React.useCallback(() => setShowDatepicker(false), [])

  const dateValue = React.useMemo(() => (inputValue ? inputValue.format(DISPLAY_FORMAT) : ''), [
    inputValue,
  ])

  React.useEffect(() => {
    const handleBeyondClick = (e: MouseEvent) => {
      setShowDatepicker(false)
      setShow(false)
    }
    document.addEventListener('click', handleBeyondClick)
    return () => document.removeEventListener('click', handleBeyondClick)
  }, [])

  React.useEffect(() => {
    if (showDatepicker) inputRef.current.parentElement?.classList.add('focused-input')
    else inputRef.current.parentElement?.classList.remove('focused-input')
  }, [showDatepicker])

  return (
    <Container onClick={stopPropagation}>
      <TextInput
        {...rest}
        ref={inputRef}
        value={dateValue}
        handleClick={handleClickInput}
        readOnly={true}
        children={{
          precomponent: <PreDecoration />,
          postcomponent: <PostDecoration />,
        }}
      />
      <DatePopover>
        <DatePicker
          show={showDatepicker}
          inputValue={inputValue}
          handleSubmit={handleSubmitDatepicker}
          handleCancel={handleClickCancel}
          selectedDate={selectedDate}
          setSelected={setSelectedDate}
        />
      </DatePopover>
      <OptionsPopover>
        <BaseList
          show={show}
          inputValue={byArrival ? OPTIONS[0] : OPTIONS[1]}
          data={OPTIONS}
          handleClick={handleClickOption}
        />
      </OptionsPopover>
    </Container>
  )
}

export default React.memo(DateDropdown)
